import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/store/useApp";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ChefHat, Store, UtensilsCrossed, User, ArrowRight, CheckCircle2, Loader2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { isStaffRole, type ClientType } from "@/lib/types";
import { toast } from "sonner";
import { completeOnboarding as completeOnboardingRequest, toAccount } from "@/lib/authApi";
import { getApiErrorMessage } from "@/lib/api";
import { formatCep, lookupCep, normalizeCep } from "@/lib/cep";
import { formatCnpj, isValidCnpj, lookupCnpj, normalizeCnpj } from "@/lib/cnpj";
import { formatCpf, isValidCpf } from "@/lib/cpf";
import { documentLabel, isPessoaFisica as isPessoaFisicaType, legalNameLabel } from "@/lib/clientTypeLabels";

const TYPES: { id: ClientType; label: string; desc: string; Icon: typeof Building2 }[] = [
  { id: "RESTAURANTE", label: "Restaurante", desc: "Cozinha de salão, bistrôs e bares", Icon: UtensilsCrossed },
  { id: "SUPERMERCADO", label: "Supermercado", desc: "Hortifrúti de varejo e mercearias", Icon: Store },
  { id: "COZINHA_INDUSTRIAL", label: "Cozinha Industrial", desc: "Refeições coletivas e catering", Icon: ChefHat },
  { id: "CLIENTE_FINAL", label: "Cliente Final", desc: "Consumidor final / pessoa física", Icon: User },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { account, authStatus, completeOnboarding, setAccount } = useApp();
  const [step, setStep] = useState<"form" | "done">("form");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cepLookupStatus, setCepLookupStatus] = useState<"idle" | "loading" | "error">("idle");
  const [cnpjLookupStatus, setCnpjLookupStatus] = useState<"idle" | "loading" | "error">("idle");
  const [companyNameLocked, setCompanyNameLocked] = useState(false);
  const [data, setData] = useState({
    companyName: "",
    tradeName: "",
    cnpj: "",
    phone: "",
    contactName: "",
    addressCep: "",
    addressLogradouro: "",
    addressNumero: "",
    addressComplemento: "",
    addressBairro: "",
    addressCidade: "",
    addressUf: "",
    clientType: "RESTAURANTE" as ClientType,
  });

  // Cliente final é pessoa física: o mesmo campo `cnpj` carrega o CPF, que é o
  // documento que o backend guarda para a conta.
  const isPessoaFisica = isPessoaFisicaType(data.clientType);
  const docLabel = documentLabel(data.clientType);
  const nameLabel = legalNameLabel(data.clientType);

  const handleClientTypeChange = (clientType: ClientType) => {
    const nextIsPessoaFisica = isPessoaFisicaType(clientType);
    if (nextIsPessoaFisica === isPessoaFisica) {
      setData((prev) => ({ ...prev, clientType }));
      return;
    }
    // CPF e CNPJ não são intercambiáveis: ao trocar a natureza do cadastro,
    // limpa o documento, o nome fantasia e a razão social vinda da consulta.
    setCnpjLookupStatus("idle");
    setData((prev) => ({
      ...prev,
      clientType,
      cnpj: "",
      tradeName: "",
      companyName: companyNameLocked ? "" : prev.companyName,
    }));
    setCompanyNameLocked(false);
  };

  const handleCepLookup = async (rawCep: string) => {
    const digits = normalizeCep(rawCep);
    if (digits.length !== 8) return;
    setCepLookupStatus("loading");
    try {
      const result = await lookupCep(digits);
      if (!result) {
        setCepLookupStatus("error");
        toast.warning("CEP não encontrado. Preencha o endereço manualmente.");
        return;
      }
      setCepLookupStatus("idle");
      setData((prev) => ({
        ...prev,
        addressLogradouro: result.logradouro || prev.addressLogradouro,
        addressBairro: result.bairro || prev.addressBairro,
        addressCidade: result.localidade || prev.addressCidade,
        addressUf: result.uf || prev.addressUf,
      }));
    } catch {
      setCepLookupStatus("error");
      toast.warning("Não foi possível consultar o CEP. Preencha o endereço manualmente.");
    }
  };

  const handleCnpjLookup = async (rawCnpj: string) => {
    const digits = normalizeCnpj(rawCnpj);
    if (digits.length !== 14) return;
    if (!isValidCnpj(digits)) {
      setCnpjLookupStatus("error");
      toast.error("CNPJ inválido. Confira os dígitos.");
      return;
    }
    setCnpjLookupStatus("loading");
    try {
      const result = await lookupCnpj(digits);
      if (!result) {
        setCnpjLookupStatus("error");
        toast.warning("CNPJ não encontrado. Preencha a razão social manualmente.");
        return;
      }
      setCnpjLookupStatus("idle");
      const company = result.razaoSocial || result.nomeFantasia || "";
      setData((prev) => ({
        ...prev,
        companyName: company,
        tradeName: result.nomeFantasia || prev.tradeName,
      }));
      setCompanyNameLocked(true);
      if (result.situacao && result.situacao.toUpperCase() !== "ATIVA") {
        toast.warning(`Situação cadastral: ${result.situacao}`);
      }
    } catch {
      setCnpjLookupStatus("error");
      toast.warning("Não foi possível consultar o CNPJ. Preencha a razão social manualmente.");
      setCompanyNameLocked(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const requiredFields: Array<{ key: keyof typeof data; label: string }> = [
      { key: "companyName", label: nameLabel },
      { key: "cnpj", label: docLabel },
      { key: "phone", label: "Telefone" },
      ...(isPessoaFisica ? [] : [{ key: "contactName" as const, label: "Contato principal" }]),
      { key: "addressCep", label: "CEP" },
      { key: "addressLogradouro", label: "Logradouro" },
      { key: "addressNumero", label: "Número" },
      { key: "addressBairro", label: "Bairro" },
      { key: "addressCidade", label: "Cidade" },
      { key: "addressUf", label: "UF" },
    ];
    const missing = requiredFields.find((field) => !String(data[field.key] ?? "").trim());
    if (missing) return toast.error(`Preencha o campo: ${missing.label}`);
    const isValidDocument = isPessoaFisica ? isValidCpf(data.cnpj) : isValidCnpj(data.cnpj);
    if (!isValidDocument) return toast.error(`${docLabel} inválido. Confira os dígitos.`);
    setIsSubmitting(true);

    // Pessoa física não tem nome fantasia e o contato principal é ela mesma.
    const payload = {
      ...data,
      tradeName: isPessoaFisica ? "" : data.tradeName.trim(),
      contactName: isPessoaFisica ? data.companyName.trim() : data.contactName,
      addressUf: data.addressUf.toUpperCase(),
    };

    try {
      const response = await completeOnboardingRequest({
        ...payload,
        tradeName: payload.tradeName || undefined,
        addressComplemento: payload.addressComplemento.trim() || undefined,
      });
      const accountResponse = toAccount(response);
      setAccount(accountResponse);
      completeOnboarding(accountResponse.profile ?? {
        ...payload,
        addressComplemento: payload.addressComplemento || null,
      });
      setStep("done");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Nao foi possivel concluir a ficha comercial"));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (authStatus !== "ready") return;
    if (!account) {
      navigate("/login", { replace: true });
      return;
    }
    // Onboarding is "done" when a profile exists. Staff users shouldn't be here
    // at all. Avoid keying on status — admins can validate before profile is
    // saved, and the old check fought AppLayout in a redirect loop.
    if (isStaffRole(account.role) || account.profile) {
      navigate("/catalogo", { replace: true });
    }
  }, [account, authStatus, navigate]);

  if (!account) return null;

  if (step === "done") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-lg w-full surface-card p-10 text-center space-y-6 animate-fade-in">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow">
            <CheckCircle2 className="h-8 w-8 text-primary-foreground" />
          </div>
          <div className="space-y-2">
            <h1 className="font-display text-3xl font-bold">Cadastro recebido!</h1>
            <p className="text-muted-foreground">
              Sua ficha comercial entrou em análise. Você já pode navegar pelo catálogo e montar pedidos —
              <span className="text-foreground font-medium"> preço e fechamento liberam após validação interna.</span>
            </p>
          </div>
          <div className="rounded-lg border border-warning/30 bg-warning/5 p-4 text-left text-sm">
            <p className="font-semibold mb-1">O que acontece agora:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Nossa equipe valida seu {docLabel} e perfil comercial</li>
              <li>• Vinculamos a tabela de preço adequada ao seu tipo de operação</li>
              <li>• Você recebe um email de confirmação em até 1 dia útil</li>
            </ul>
          </div>
          <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/catalogo")}>
            Explorar catálogo <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-surface-elevated">
        <div className="container h-16 flex items-center justify-between">
          <Logo />
          <span className="text-xs text-muted-foreground">Passo 1 de 1 · Ficha comercial</span>
        </div>
      </header>

      <div className="container max-w-2xl py-10 lg:py-14">
        <div className="space-y-2 mb-8">
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            Conta criada. Falta a ficha comercial.
          </h1>
          <p className="text-muted-foreground">
            Esses dados ajudam a equipe Naturexpress a vincular você à tabela e janela de entrega corretas.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8">
          <section className="surface-card p-6 space-y-4">
            <h2 className="font-display font-semibold text-lg">Tipo de operação</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {TYPES.map(({ id, label, desc, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleClientTypeChange(id)}
                  className={cn(
                    "rounded-xl border p-4 text-left transition-all",
                    data.clientType === id
                      ? "border-primary bg-primary/5 shadow-sm"
                      : "border-border bg-surface hover:border-primary/30",
                  )}
                >
                  <Icon className={cn("h-5 w-5 mb-3", data.clientType === id ? "text-primary" : "text-muted-foreground")} />
                  <p className="font-display font-semibold text-sm">{label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="surface-card p-6 space-y-4">
            <h2 className="font-display font-semibold text-lg">
              {isPessoaFisica ? "Dados pessoais" : "Dados da empresa"}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="documento">{docLabel} *</Label>
                <div className="relative">
                  <Input
                    id="documento"
                    value={data.cnpj}
                    inputMode="numeric"
                    maxLength={isPessoaFisica ? 14 : 18}
                    onChange={(e) => {
                      if (isPessoaFisica) {
                        setData((prev) => ({ ...prev, cnpj: formatCpf(e.target.value) }));
                        return;
                      }
                      const formatted = formatCnpj(e.target.value);
                      setData((prev) => ({ ...prev, cnpj: formatted }));
                      if (normalizeCnpj(formatted).length === 14) {
                        void handleCnpjLookup(formatted);
                      } else if (companyNameLocked) {
                        setCompanyNameLocked(false);
                      }
                    }}
                    placeholder={isPessoaFisica ? "000.000.000-00" : "00.000.000/0000-00"}
                  />
                  {!isPessoaFisica && cnpjLookupStatus === "loading" && (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="companyName">{nameLabel} *</Label>
                <div className="relative">
                  <Input
                    id="companyName"
                    value={data.companyName}
                    onChange={(e) => setData({ ...data, companyName: e.target.value })}
                    placeholder={
                      isPessoaFisica
                        ? "Nome completo, como no documento"
                        : "Preencha o CNPJ acima para buscar automaticamente"
                    }
                    readOnly={companyNameLocked}
                    className={cn(companyNameLocked && "bg-muted/50 pr-12")}
                  />
                  {companyNameLocked && (
                    <button
                      type="button"
                      onClick={() => setCompanyNameLocked(false)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Editar razão social"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Editar
                    </button>
                  )}
                </div>
              </div>
              {!isPessoaFisica && (
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="tradeName">Nome fantasia</Label>
                  <Input
                    id="tradeName"
                    value={data.tradeName}
                    onChange={(e) => setData({ ...data, tradeName: e.target.value })}
                    placeholder="Nome usado nas listas (opcional)"
                  />
                </div>
              )}
              <div className={cn("space-y-2", isPessoaFisica && "sm:col-span-2")}>
                <Label>Telefone *</Label>
                <Input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} placeholder="(11) 99999-0000" />
              </div>
              {!isPessoaFisica && (
                <div className="space-y-2">
                  <Label>Contato principal *</Label>
                  <Input value={data.contactName} onChange={(e) => setData({ ...data, contactName: e.target.value })} placeholder="Nome do responsável" />
                </div>
              )}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="cep">CEP *</Label>
                <div className="relative">
                  <Input
                    id="cep"
                    value={data.addressCep}
                    inputMode="numeric"
                    maxLength={9}
                    onChange={(e) => {
                      const formatted = formatCep(e.target.value);
                      setData({ ...data, addressCep: formatted });
                      if (normalizeCep(formatted).length === 8) {
                        void handleCepLookup(formatted);
                      }
                    }}
                    onBlur={() => void handleCepLookup(data.addressCep)}
                    placeholder="00000-000"
                  />
                  {cepLookupStatus === "loading" && (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Logradouro *</Label>
                <Input
                  value={data.addressLogradouro}
                  onChange={(e) => setData({ ...data, addressLogradouro: e.target.value })}
                  placeholder="Rua, avenida, etc."
                />
              </div>
              <div className="space-y-2">
                <Label>Número *</Label>
                <Input
                  value={data.addressNumero}
                  onChange={(e) => setData({ ...data, addressNumero: e.target.value })}
                  placeholder="123"
                />
              </div>
              <div className="space-y-2">
                <Label>Complemento</Label>
                <Input
                  value={data.addressComplemento}
                  onChange={(e) => setData({ ...data, addressComplemento: e.target.value })}
                  placeholder="Sala, andar, bloco..."
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Bairro *</Label>
                <Input
                  value={data.addressBairro}
                  onChange={(e) => setData({ ...data, addressBairro: e.target.value })}
                  placeholder="Bairro"
                />
              </div>
              <div className="space-y-2">
                <Label>Cidade *</Label>
                <Input
                  value={data.addressCidade}
                  onChange={(e) => setData({ ...data, addressCidade: e.target.value })}
                  placeholder="Cidade"
                />
              </div>
              <div className="space-y-2">
                <Label>UF *</Label>
                <Input
                  value={data.addressUf}
                  onChange={(e) => setData({ ...data, addressUf: e.target.value.toUpperCase().slice(0, 2) })}
                  maxLength={2}
                  placeholder="SP"
                />
              </div>
            </div>
          </section>

          <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Concluindo..." : "Concluir cadastro"}
            {!isSubmitting && <ArrowRight className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
